// Accountmanager, projectleiders, projectmanagement en preferred crew aanpassen.
//
// Joris, 10 sep 2026: "ik kan accountmanager niet aanpassen van een account" —
// het stond alleen te lezen. Accountmanager en projectleiders zijn koppelingen
// naar Personen, dus die kiezen we uit een lijst en slaan we op als record-ids;
// de twee andere velden zijn vrije tekst.
import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';

export default function TeamBewerkDialog({ accountId, account, onClose, onOpgeslagen }) {
  const [personen, setPersonen] = useState([]);
  const [laden, setLaden] = useState(true);
  const [bezig, setBezig] = useState(false);
  const [vorm, setVorm] = useState({
    accountmanagerIds: account?.accountmanagerIds || [],
    projectleiderIds: account?.projectleiderIds || [],
    projectmanagement: account?.projectmanagement || '',
    preferredCrew: account?.preferredCrew || '',
  });

  useEffect(() => {
    authFetch(`${BACKEND_URL}/Api/klanten/personen/keuze`)
      .then((r) => leesJson(r, 'Personen laden'))
      .then((d) => setPersonen(d.personen || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLaden(false));
  }, []);

  const opId = useMemo(() => new Map(personen.map((p) => [p.id, p])), [personen]);
  const gekozen = (ids) => (ids || []).map((id) => opId.get(id) || { id, naam: id });

  const bewaar = async () => {
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/klanten/${accountId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vorm),
      });
      const d = await leesJson(r, 'Account opslaan');
      toast.success(d.naarAirtable
        ? 'Opgeslagen'
        : 'Opgeslagen — op deze omgeving gaat het nog niet door naar Airtable');
      onOpgeslagen?.(d.account?.account || null);
      onClose();
    } catch (e) {
      toast.error(`Opslaan mislukt: ${e.message}`);
    } finally {
      setBezig(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Team van dit account</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
        {laden ? <CircularProgress size={22} /> : (
          <>
            <Autocomplete
              multiple options={personen} getOptionLabel={(p) => p.naam || p.id}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={gekozen(vorm.accountmanagerIds)}
              onChange={(_, v) => setVorm((f) => ({ ...f, accountmanagerIds: v.map((p) => p.id) }))}
              renderTags={(waarden, getTagProps) => waarden.map((p, i) => (
                <Chip {...getTagProps({ index: i })} key={p.id} label={p.naam} size="small" />
              ))}
              renderInput={(params) => <TextField {...params} size="small" label="Accountmanager" />}
            />
            <Autocomplete
              multiple options={personen} getOptionLabel={(p) => p.naam || p.id}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={gekozen(vorm.projectleiderIds)}
              onChange={(_, v) => setVorm((f) => ({ ...f, projectleiderIds: v.map((p) => p.id) }))}
              renderTags={(waarden, getTagProps) => waarden.map((p, i) => (
                <Chip {...getTagProps({ index: i })} key={p.id} label={p.naam} size="small" />
              ))}
              renderInput={(params) => <TextField {...params} size="small" label="Projectleiders" />}
            />
            <TextField
              label="Projectmanagement" size="small" multiline minRows={4}
              value={vorm.projectmanagement}
              onChange={(e) => setVorm((f) => ({ ...f, projectmanagement: e.target.value }))}
              helperText="Vrije tekst, één afspraak per regel — bijvoorbeeld 'Video: Boy'."
            />
            <TextField
              label="Preferred crew" size="small" multiline minRows={2}
              value={vorm.preferredCrew}
              onChange={(e) => setVorm((f) => ({ ...f, preferredCrew: e.target.value }))}
            />
            <Typography variant="caption" color="text.secondary">
              Wijzigingen gaan naar Airtable; de pagina toont ze meteen.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" onClick={bewaar} disabled={bezig || laden} sx={{ textTransform: 'none' }}>
          {bezig ? <CircularProgress size={18} /> : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
